// Progress Section
import { History, AlertCircle, Star, Target } from "lucide-react";
import {
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useTranslation } from "react-i18next";

// Custom Pie Chart Component using CSS
const CustomPieChart = ({ data }) => {
  const { t } = useTranslation();
  // A palette of distinct colors for the chart slices
  const COLORS = ["#3b82f6", "#16a34a", "#f97316", "#ef4444"]; // Blue, Green, Orange, Red

  // This check prevents errors if data is not available
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500">
        {t("progress.noInterestData")}
      </div>
    );
  }

  return (
    <div className="w-full h-64">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Tooltip
            contentStyle={{
              background: "white",
              border: "1px solid #e2e8f0",
              borderRadius: "0.5rem",
              boxShadow:
                "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
            }}
          />
          <Legend iconType="circle" verticalAlign="bottom" />
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={85} // Slightly larger for better visuals
            fill="#8884d8" // A default fill, which will be overridden by <Cell>
            labelLine={false}
            // This label function correctly places the percentage on each slice
            label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
          >
            {/* This is the key part: It maps over your data and assigns
                a unique color from the COLORS array to each <Cell>. */}
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
                stroke="white"
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

const ProgressSection = ({ report }) => {
  const { t } = useTranslation();
  // Defensive checks
  const historicalInterest =
    report?.progress_ai?.historical_interest_by_subject || {};

  // Get all week keys
  const weekKeys = Object.keys(historicalInterest);

  // Pick the latest one (assuming keys are chronological like "2025-W37")
  const latestWeek = weekKeys.length > 0 ? weekKeys.sort().at(-1) : null;

  // Get the data for that week
  const interestData = latestWeek ? historicalInterest[latestWeek] : {};

  // Prepare chart data
  const pieChartData = Object.entries(interestData).map(
    ([subject, interest]) => ({
      name: subject,
      value: interest,
    })
  );

  // console.log("intrest", historicalInterest, interestData, pieChartData);

  const highInterestSubjects =
    report?.progress_ai?.high_interest_subjects || [];
  const focusTopicsStruggle = report?.progress_ai?.focus_topics_struggle || [];
  const historicalStruggles = report?.progress_ai?.historical_struggles || {};
  const latestInsight =
    report?.progress_ai?.latest_insight || t("progress.noInsightsAvailable");

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
      <div className="flex items-center space-x-2 mb-4">
        <Target className="w-6 h-6 text-purple-600" />
        <h3 className="text-xl font-bold">{t("progress.learningProgress")}</h3>
      </div>

      <p className="text-sm text-slate-600 mb-6 border-l-4 border-purple-200 pl-4">
        {latestInsight}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Column: Insights & Lists */}
        <div className="lg:col-span-2 space-y-6">
          {/* High Interest Card */}
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h4 className="font-semibold mb-3 flex items-center text-green-400">
              <Star className="w-5 h-5 mr-2 text-blue-600" />  {t("progress.highInterestSubjects")}
            </h4>
            <div className="flex flex-wrap gap-2">
              {highInterestSubjects.length > 0 ? (
                highInterestSubjects.map((subject, index) => (
                  <span
                    key={index}
                    className="bg-green-200 text-green-800 px-3 py-1 rounded-full text-sm font-medium"
                  >
                    {subject}
                  </span>
                ))
              ) : (
                <span className="text-sm text-slate-500">
                   {t("progress.noHighInterestSubjects")}
                </span>
              )}
            </div>
          </div>

          {/* Struggles Card */}
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h4 className="font-semibold mb-3 flex items-center text-red-800">
              <AlertCircle className="w-5 h-5 mr-2 text-red-600" /> {t("progress.focusAreasStruggles")}
              (Struggles)
            </h4>
            <div className="flex flex-wrap gap-2">
              {focusTopicsStruggle.length > 0 ? (
                focusTopicsStruggle.map((topic, index) => (
                  <span
                    key={index}
                    className="bg-red-200 text-red-800 px-3 py-1 rounded-full text-sm font-medium"
                  >
                    {topic}
                  </span>
                ))
              ) : (
                <span className="text-sm text-slate-500">
                  {t("progress.noFocusTopics")}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Chart */}
        <div className="lg:col-span-3 bg-slate-50 p-4 rounded-lg border border-slate-200">
          <h4 className="font-semibold text-center mb-2 text-slate-700">
            {t("progress.weeklyInterestDistribution")}
          </h4>
          {pieChartData.length > 0 ? (
            <CustomPieChart data={pieChartData} />
          ) : (
            <p className="text-sm text-slate-500 text-center">
              {t("progress.noInterestDataAvailable")}
            </p>
          )}
        </div>
      </div>

      {/* Historical Struggles Section */}
      <div className="mt-6">
        <h4 className="font-semibold mb-3 flex items-center text-slate-700">
          <History className="w-5 h-5 mr-2" /> {t("progress.historicalStruggles")}
        </h4>
        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3">
          {Object.keys(historicalStruggles).length > 0 ? (
            Object.entries(historicalStruggles).map(([week, struggles]) => (
              <div key={week} className="flex items-center">
                <span className="font-bold text-sm text-slate-600 w-20">
                  {week}:
                </span>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(struggles).length > 0 ? (
                    Object.entries(struggles).map(([topic, count]) => (
                      <span
                        key={topic}
                        className="bg-orange-200 text-orange-800 text-xs font-semibold px-2.5 py-1 rounded-full"
                      >
                         {topic} ({t("progress.interactions")}: {count})
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-slate-500">
                       {t("progress.noSpecificStruggles")}
                    </span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-slate-500">
              {t("progress.noHistoricalStruggles")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgressSection;
